/**
 * WorkflowAdvanced
 *
 * "Advanced" drawer that provides escape-hatch access to raw job data
 * and tab-style views for power users who need direct access.
 *
 * Part of Phase 8.4: Polish & Advanced Features
 */

import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Settings2,
  FileJson,
  MapPin,
  FileText,
  ClipboardCheck,
  History,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { JobData } from './WorkflowContainer';
import type { JobWorkflowState } from '@/types/workflow';

interface WorkflowAdvancedProps {
  jobId: string;
  contractorId: string;
  jobData: JobData;
  workflowState: JobWorkflowState | null;
  onNavigateToJobDetail?: () => void;
}

export function WorkflowAdvanced({
  jobId,
  contractorId,
  jobData,
  workflowState,
  onNavigateToJobDetail,
}: WorkflowAdvancedProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Format JSON for display
  const formatJson = (obj: unknown) => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground"
          title="Advanced options"
        >
          <Settings2 className="h-4 w-4" />
          <span className="ml-2 hidden lg:inline">Advanced</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Advanced Options
          </SheetTitle>
          <SheetDescription>
            Power user tools and raw data access
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="data" className="mt-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="data" className="text-xs">
              <FileJson className="h-3 w-3 mr-1" />
              Data
            </TabsTrigger>
            <TabsTrigger value="jurisdiction" className="text-xs">
              <MapPin className="h-3 w-3 mr-1" />
              AHJ
            </TabsTrigger>
            <TabsTrigger value="workflow" className="text-xs">
              <ClipboardCheck className="h-3 w-3 mr-1" />
              State
            </TabsTrigger>
            <TabsTrigger value="actions" className="text-xs">
              <Settings2 className="h-3 w-3 mr-1" />
              Actions
            </TabsTrigger>
          </TabsList>

          {/* Raw Job Data */}
          <TabsContent value="data" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileJson className="h-4 w-4" />
                  Raw Job Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px] rounded-md border bg-muted/30 p-4">
                  <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                    {formatJson({
                      id: jobData.id,
                      title: jobData.title,
                      description: jobData.description,
                      location: jobData.location,
                      jurisdictionId: jobData.jurisdictionId,
                      jurisdictionCode: jobData.jurisdictionCode,
                      jurisdictionAhjName: jobData.jurisdictionAhjName,
                      jurisdictionOverlays: jobData.jurisdictionOverlays,
                      permitType: jobData.permitType,
                      jobScope: jobData.jobScope,
                      trade: jobData.trade,
                      estimatedValue: jobData.estimatedValue,
                      propertyType: jobData.propertyType,
                      contractorRole: jobData.contractorRole,
                      client_name: jobData.client_name,
                      client_email: jobData.client_email,
                      client_phone: jobData.client_phone,
                    })}
                  </pre>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jurisdiction Info */}
          <TabsContent value="jurisdiction" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Jurisdiction Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">AHJ Name</span>
                    <p className="font-medium">
                      {jobData.jurisdictionAhjName || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Code</span>
                    <p className="font-medium font-mono">
                      {jobData.jurisdictionCode || 'Not set'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ID</span>
                    <p className="font-medium font-mono">
                      {jobData.jurisdictionId || 'Not set'}
                    </p>
                  </div>
                </div>

                {/* Overlays */}
                {jobData.jurisdictionOverlays && (
                  <div>
                    <span className="text-sm text-muted-foreground">
                      Overlays
                    </span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(jobData.jurisdictionOverlays).map(
                        ([key, value]) =>
                          value && (
                            <Badge
                              key={key}
                              variant="outline"
                              className={cn(
                                'text-xs',
                                key.includes('hvhz') || key.includes('hurricane')
                                  ? 'border-red-500 text-red-600'
                                  : key.includes('flood')
                                  ? 'border-muted-foreground/30 text-muted-foreground'
                                  : key.includes('coastal')
                                  ? 'border-muted-foreground/30 text-muted-foreground'
                                  : ''
                              )}
                            >
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </Badge>
                          )
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Workflow State */}
          <TabsContent value="workflow" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" />
                  Workflow State
                </CardTitle>
              </CardHeader>
              <CardContent>
                {workflowState ? (
                  <ScrollArea className="h-[400px] rounded-md border bg-muted/30 p-4">
                    <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                      {formatJson({
                        currentStepId: workflowState.currentStepId,
                        currentSubstepId: workflowState.currentSubstepId,
                        stepStatus: workflowState.stepStatus,
                        completedSubsteps: workflowState.completedSubsteps,
                        progress: workflowState.progress,
                        blockers: workflowState.blockers.length,
                        warnings: workflowState.warnings.length,
                        info: workflowState.info.length,
                        lastUpdatedAt: workflowState.lastUpdatedAt,
                        lastComplianceCheckAt: workflowState.lastComplianceCheckAt,
                      })}
                    </pre>
                  </ScrollArea>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Workflow state not loaded
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quick Actions */}
          <TabsContent value="actions" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="h-4 w-4" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Navigate to classic view */}
                {onNavigateToJobDetail && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      setIsOpen(false);
                      onNavigateToJobDetail();
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Classic Job View
                  </Button>
                )}

                {/* Copy Job ID */}
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    navigator.clipboard.writeText(jobId);
                  }}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Copy Job ID
                </Button>

                {/* Copy Firestore Path */}
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `contractors/${contractorId}/jobs/${jobId}`
                    );
                  }}
                >
                  <FileJson className="h-4 w-4 mr-2" />
                  Copy Firestore Path
                </Button>

                {/* Debug info */}
                <div className="pt-4 border-t text-xs text-muted-foreground space-y-1">
                  <p>
                    <span className="font-medium">Job ID:</span>{' '}
                    <code className="bg-muted px-1 rounded">{jobId}</code>
                  </p>
                  <p>
                    <span className="font-medium">Contractor ID:</span>{' '}
                    <code className="bg-muted px-1 rounded">{contractorId}</code>
                  </p>
                  {workflowState?.lastUpdatedAt && (
                    <p>
                      <span className="font-medium">Last Updated:</span>{' '}
                      {new Date(workflowState.lastUpdatedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
